import {
  AlertTriangle,
  ArrowUpDown,
  ChevronRight,
  Edit,
  GripVertical,
  Image as ImageIcon,
  Package,
  Plus,
  Trash2,
} from 'lucide-react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useCategories } from '../hooks/useCategories';
import { CategoryService } from '../services/categoryService';
import type { Category, CreateCategoryInput, UpdateCategoryInput } from '../services/categoryService';
import { FileService } from '../services/fileService';
import { compressImageFile } from '../utils/imageCompression';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function CategoriesManagement() {
  const navigate = useNavigate();

  const {
    categories,
    isLoading: isCategoriesLoading,
    error: categoriesError,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useCategories();

  // Dialog states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Selection states
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  // Form states
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<CreateCategoryInput>({
    name: '',
    imageUrl: '',
  });

  // Ordering states
  const [orderedCategories, setOrderedCategories] = useState<Category[]>([]);
  const [isReordering, setIsReordering] = useState(false);

  // Keep a local ordered copy in sync with the fetched categories
  useEffect(() => {
    if (categories) {
      setOrderedCategories(categories);
    }
  }, [categories]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const items = Array.from(orderedCategories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for smooth UX
    setOrderedCategories(items);

    try {
      setIsReordering(true);
      const reorderData = items.map((category, index) => ({
        id: category.id,
        order: index + 1,
      }));
      await CategoryService.reorderCategories(reorderData);
      toast.success('Categories reordered successfully');
    } catch (error) {
      console.error('Error reordering categories:', error);
      toast.error('Failed to reorder categories');
      // Revert to server order on failure
      if (categories) setOrderedCategories(categories);
    } finally {
      setIsReordering(false);
    }
  };

  const handleOpenForm = (category?: Category) => {
    if (category) {
      setSelectedCategory(category);
      setFormData({
        name: category.name,
        imageUrl: category.imageUrl,
      });
    } else {
      setSelectedCategory(null);
      setFormData({
        name: '',
        imageUrl: '',
      });
    }
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.imageUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = { name: formData.name.trim(), imageUrl: formData.imageUrl };
      if (selectedCategory) {
        await updateCategory.mutateAsync({
          id: selectedCategory.id,
          category: payload as UpdateCategoryInput,
        });
        toast.success('Category updated successfully');
      } else {
        await createCategory.mutateAsync(payload);
        toast.success('Category created successfully');
      }
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    try {
      await deleteCategory.mutateAsync(categoryToDelete.id);
      toast.success('Category deleted successfully');
      setIsDeleteOpen(false);
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const compressed = await compressImageFile(file);
      const response = await FileService.uploadFiles([compressed]);
      setFormData(prev => ({ ...prev, imageUrl: response.files[0].url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleManageProducts = (category: Category) => {
    navigate(`/categories/${category.id}/products`);
  };

  if (categoriesError) {
    return (
      <div className="p-8 text-center text-red-500 bg-red-50 rounded-lg mx-6 mt-6">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
        <h3 className="text-lg font-semibold">Error loading data</h3>
        <p>{categoriesError?.message}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Categories</h1>
          <p className="text-gray-500 mt-1">Manage your product categories and organization</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {orderedCategories.length > 1 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm text-gray-600">
              <ArrowUpDown className="w-4 h-4" />
              <span>Drag to reorder</span>
            </div>
          )}
          <Button onClick={() => handleOpenForm()} className="flex-1 sm:flex-none">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isCategoriesLoading && (
        <div className="w-full h-64 bg-gray-50 animate-pulse rounded-xl flex items-center justify-center">
          <div className="h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Empty State */}
      {!isCategoriesLoading && orderedCategories.length === 0 && (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
          <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-blue-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">No categories yet</h2>
          <p className="text-gray-500 mt-2 mb-8 max-w-md mx-auto">
            Create your first category to start organizing your products effectively.
          </p>
          <Button onClick={() => handleOpenForm()} size="lg">
            <Plus className="w-5 h-5 mr-2" />
            Create Category
          </Button>
        </div>
      )}

      {/* Categories List */}
      {!isCategoriesLoading && orderedCategories.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="categories">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={`space-y-3 transition-all duration-200 ${
                  snapshot.isDraggingOver ? 'bg-blue-50/50 rounded-2xl p-2' : ''
                }`}
              >
                {orderedCategories.map((category, index) => (
                  <Draggable key={category.id} draggableId={category.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`bg-white rounded-lg border border-gray-200 overflow-hidden transition-all duration-200 ${
                          dragSnapshot.isDragging ? 'shadow-xl ring-2 ring-blue-200' : 'hover:border-gray-300'
                        } ${isReordering ? 'opacity-60' : ''}`}
                      >
                        {/* Mobile card view */}
                        <div className="sm:hidden">
                          <div className="p-4 flex items-center gap-2">
                            <div
                              {...dragProvided.dragHandleProps}
                              className="flex-shrink-0 p-1 rounded-lg hover:bg-gray-50 cursor-grab active:cursor-grabbing"
                              title="Drag to reorder"
                            >
                              <GripVertical className="h-4 w-4 text-gray-300" />
                            </div>
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 flex-shrink-0">
                              {index + 1}
                            </span>
                            {category.imageUrl ? (
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                className="h-12 w-12 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200 flex-shrink-0">
                                <Package className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">{category.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{category.productIds?.length || 0} products</div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                                onClick={() => handleOpenForm(category)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:bg-red-50"
                                onClick={() => handleDeleteClick(category)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {/* Bottom: highlighted manage products strip */}
                          <button
                            className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 transition-colors border-t border-blue-100"
                            onClick={() => handleManageProducts(category)}
                          >
                            <span className="text-sm font-medium text-blue-700">Add / Remove Products</span>
                            <ChevronRight className="h-4 w-4 text-blue-500" />
                          </button>
                        </div>

                        {/* Desktop row view */}
                        <div className="hidden sm:flex items-center gap-4 px-6 py-4">
                          <div
                            {...dragProvided.dragHandleProps}
                            className="flex-shrink-0 p-2 rounded-xl hover:bg-gray-50 cursor-grab active:cursor-grabbing"
                            title="Drag to reorder"
                          >
                            <GripVertical className="h-5 w-5 text-gray-300 hover:text-gray-400" />
                          </div>
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-blue-50 text-blue-700 border border-blue-100 flex-shrink-0">
                            {index + 1}
                          </span>
                          {category.imageUrl ? (
                            <img
                              src={category.imageUrl}
                              alt={category.name}
                              className="h-10 w-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200 flex-shrink-0">
                              <Package className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{category.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{category.productIds?.length || 0} products</div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 flex-shrink-0"
                            onClick={() => handleManageProducts(category)}
                          >
                            <Package className="w-3 h-3 mr-1.5" />
                            Add / Remove Products
                          </Button>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-600 hover:text-blue-900 hover:bg-blue-50"
                              onClick={() => handleOpenForm(category)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteClick(category)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="space-y-6 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Category Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Summer Collection"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Cover Image</label>
              <div
                className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:bg-gray-50 transition-colors cursor-pointer relative"
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                <input
                  id="image-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                {formData.imageUrl ? (
                  <div className="relative aspect-video w-full overflow-hidden rounded-md">
                    <img
                      src={formData.imageUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white font-medium">Change Image</span>
                    </div>
                  </div>
                ) : (
                  <div className="py-8">
                    <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ImageIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <p className="text-sm text-gray-600 font-medium">Click to upload image</p>
                    <p className="text-xs text-gray-400 mt-1">Images are compressed on upload</p>
                  </div>
                )}
                {isUploading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                      <span className="text-xs text-gray-600 font-medium">Compressing...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUploading}>
                {selectedCategory ? 'Save Changes' : 'Create Category'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <div className="flex flex-col items-center text-center p-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <DialogTitle className="text-xl mb-2">Delete Category?</DialogTitle>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete <span className="font-semibold text-gray-900">{categoryToDelete?.name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3 w-full">
              <Button variant="outline" className="flex-1" onClick={() => setIsDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
} 
